import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Hash, Leaf } from 'lucide-react';
import api from '../../utils/api';

// Cache for PLU lookups to avoid repeated API calls
const pluCache = new Map();

/**
 * PLUBadge - Display PLU code for produce items
 * 
 * Features:
 * - Auto-matches item names to PLU codes
 * - Shows organic variant (9-prefix)
 * - Tooltip with full PLU details
 * - Caches lookups for performance
 */
export function PLUBadge({ itemName, isOrganic = false, className = '' }) {
  const [pluData, setPluData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemName) return;
    
    // Check cache first
    const cacheKey = itemName.toLowerCase();
    if (pluCache.has(cacheKey)) {
      setPluData(pluCache.get(cacheKey));
      return;
    }

    // Search for PLU code
    const searchPLU = async () => {
      setLoading(true);
      try {
        // Extract just the produce name (remove quantity, size words)
        const cleanName = extractProduceName(itemName);
        if (!cleanName) {
          setPluData(null);
          return;
        }

        const response = await api.get(`/internal/lookup/plu/search?query=${encodeURIComponent(cleanName)}&limit=1`);
        
        if (response.data?.results?.length > 0) {
          const result = response.data.results[0];
          pluCache.set(cacheKey, result);
          setPluData(result);
        } else {
          pluCache.set(cacheKey, null);
          setPluData(null);
        }
      } catch (error) {
        console.error('PLU lookup failed:', error);
        setPluData(null);
      } finally {
        setLoading(false);
      }
    };

    searchPLU();
  }, [itemName]);

  if (loading || !pluData) {
    return null;
  }

  const displayPLU = isOrganic ? pluData.organic_plu : pluData.plu;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`text-xs cursor-help ${isOrganic ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50'} ${className}`}
            data-testid="plu-badge"
          >
            {isOrganic && <Leaf className="w-3 h-3 mr-1" />}
            <Hash className="w-3 h-3 mr-0.5" />
            {displayPLU}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{pluData.name}</p>
            <div className="text-xs text-gray-500 space-y-0.5">
              <p>PLU: {pluData.plu} {pluData.organic_plu && `(Organic: ${pluData.organic_plu})`}</p>
              <p>Category: {pluData.subcategory || pluData.category}</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * PLUDisplay - Static PLU display without lookup
 * Use when PLU is already known
 */
export function PLUDisplay({ plu, organicPlu, name, subcategory, isOrganic = false, className = '' }) {
  const displayPLU = isOrganic && organicPlu ? organicPlu : plu;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`text-xs cursor-help ${isOrganic ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50'} ${className}`}
            data-testid="plu-display"
          >
            {isOrganic && <Leaf className="w-3 h-3 mr-1" />}
            <Hash className="w-3 h-3 mr-0.5" />
            {displayPLU}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-medium">{name}</p>
            {subcategory && <p className="text-xs text-gray-500">{subcategory}</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Helper to extract produce name from item description
 * Removes quantities, sizes, packaging info
 */
function extractProduceName(itemName) {
  if (!itemName) return null;
  
  // Common words to remove
  const removeWords = [
    'organic', 'fresh', 'local', 'premium', 'natural',
    'lb', 'lbs', 'oz', 'ounce', 'pound', 'kg', 'gram',
    'bunch', 'bag', 'pack', 'package', 'container',
    'small', 'medium', 'large', 'xl', 'extra',
    'whole', 'sliced', 'cut', 'diced', 'chopped'
  ];
  
  // Remove numbers and common words
  let cleaned = itemName.toLowerCase()
    .replace(/[\d.]+\s*(lb|lbs|oz|kg|g|bunch|pack|ct|count)?/gi, '')
    .replace(new RegExp(`\\b(${removeWords.join('|')})\\b`, 'gi'), '')
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Return if we have something meaningful
  return cleaned.length >= 3 ? cleaned : null;
}

/**
 * ProduceCategoryBadge - Show category based on PLU lookup
 */
export function ProduceCategoryBadge({ itemName, className = '' }) {
  const [category, setCategory] = useState(null);

  useEffect(() => {
    if (!itemName) return;

    const cacheKey = `cat_${itemName.toLowerCase()}`;
    if (pluCache.has(cacheKey)) {
      setCategory(pluCache.get(cacheKey));
      return;
    }

    const lookup = async () => {
      try {
        const cleanName = extractProduceName(itemName);
        if (!cleanName) return;

        const response = await api.get(`/internal/lookup/plu/search?query=${encodeURIComponent(cleanName)}&limit=1`);
        
        if (response.data?.results?.length > 0) {
          const cat = response.data.results[0].subcategory || response.data.results[0].category;
          pluCache.set(cacheKey, cat);
          setCategory(cat);
        }
      } catch (error) {
        // Silently fail
      }
    };

    lookup();
  }, [itemName]);

  if (!category) return null;

  return (
    <Badge variant="outline" className={`text-xs bg-purple-50 border-purple-200 text-purple-700 ${className}`}>
      {category}
    </Badge>
  );
}

export default PLUBadge;
